'use client';
// ──────────────────────────────────────────────────────
// useBarcodeScanner — Scanner-agnostic HID barcode hook
//
// Compatible with: EZPOS, Honeywell, Datalogic, Zebra,
// Symbol, and any keyboard-wedge / USB-HID scanner.
//
// Detection strategy:
//   1. Buffer printable chars arriving rapidly
//   2. Fire on explicit terminator (Enter / Tab)
//   3. Auto-fire on silence timeout if buffer >= minLength
//      (handles scanners with no suffix configured)
//   4. Reset buffer if typing is too slow (human typing)
//
// Timing constants tuned for broad scanner compatibility:
//   - IDLE_RESET_MS  (300ms) — gap that resets the buffer
//   - AUTO_FIRE_MS   (100ms) — silence after last char → fire
// ──────────────────────────────────────────────────────
import { useEffect, useRef, useCallback } from 'react';

const IDLE_RESET_MS = 300;            // human typing gap → clear buffer
const AUTO_FIRE_MS = 100;             // silence after last char → auto-fire
const MIN_BARCODE_LENGTH = 6;

// Characters allowed in barcode input (digits, letters, Arabic digits, and
// common barcode special chars used by GS1-128, Code128, etc.)
const BARCODE_CHAR_RE = /[a-zA-Z0-9\u0660-\u0669\u06F0-\u06F9\-./+]/;

// Simple beep via Web Audio API
function playBeep() {
    try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 1200;
        gain.gain.value = 0.08;
        osc.start();
        osc.stop(ctx.currentTime + 0.08);
    } catch { /* ignore if AudioContext unavailable */ }
}

interface UseBarcodeOptions {
    onScan: (barcode: string) => void;
    enabled?: boolean;
    minLength?: number;
}

export function useBarcodeScanner({ onScan, enabled = true, minLength = MIN_BARCODE_LENGTH }: UseBarcodeOptions) {
    const buffer = useRef('');
    const lastKeyTime = useRef(0);
    const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoFireTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = useCallback(() => {
        if (resetTimer.current) { clearTimeout(resetTimer.current); resetTimer.current = null; }
        if (autoFireTimer.current) { clearTimeout(autoFireTimer.current); autoFireTimer.current = null; }
    }, []);

    const clearBuffer = useCallback(() => {
        buffer.current = '';
        clearTimers();
    }, [clearTimers]);

    /** Flush the buffer: if it meets minimum length, fire onScan. Always clear. */
    const flushBuffer = useCallback(() => {
        const code = buffer.current.trim();
        clearBuffer();

        if (code.length >= minLength) {
            playBeep();
            onScan(code);
        }
    }, [minLength, onScan, clearBuffer]);

    useEffect(() => {
        if (!enabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if user is typing in an input/textarea
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            // Ignore modifiers
            if (e.ctrlKey || e.metaKey || e.altKey) return;

            // ── Terminator keys: Enter or Tab ────────────────
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                flushBuffer();
                return;
            }

            // Only buffer valid barcode characters
            if (e.key.length !== 1) return;
            if (!BARCODE_CHAR_RE.test(e.key)) return;

            const now = Date.now();
            const gap = now - lastKeyTime.current;
            lastKeyTime.current = now;

            // If gap too large → human typing, reset and start fresh
            if (buffer.current.length > 0 && gap > IDLE_RESET_MS) {
                clearBuffer();
            }

            buffer.current += e.key;

            // ── Clear any existing timers ─────────────────────
            clearTimers();

            // ── Idle reset timer: clear buffer if no more input ─
            resetTimer.current = setTimeout(clearBuffer, IDLE_RESET_MS);

            // ── Auto-fire timer: fire if buffer meets minLength ─
            // Handles scanners with no suffix (no Enter/Tab sent).
            // Short silence after rapid input → treat as complete scan.
            if (buffer.current.length >= minLength) {
                autoFireTimer.current = setTimeout(flushBuffer, AUTO_FIRE_MS);
            }
        };

        window.addEventListener('keydown', handleKeyDown, { capture: true });

        return () => {
            window.removeEventListener('keydown', handleKeyDown, { capture: true });
            clearTimers();
        };
    }, [enabled, minLength, flushBuffer, clearBuffer, clearTimers]);
}
